import { useNavigation } from "@react-navigation/native";
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  View,
  TouchableNativeFeedback,
  ScrollView,
} from "react-native";
import Plus from "@assets/svg/MyDeliveries/Plus";
import Minus from "@assets/svg/MyDeliveries/Minus";
import CustomText from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import colors from "@constants/color";
import { itemsQuantityStatusV3 } from "@services/api";
import toasts from "@utils/toast";
import IssuesImagesUpload from "@screens/MyDeliveries/components/IssuesImagesUpload";
import transformMarkingArrToObj from "@screens/MyDeliveries/markingConfig";
import { getStateDiff } from "@utils/itemMarking";

function MultiQuantityIssues({ route }) {
  const navigation = useNavigation();
  const markingInProcess = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [itemNotFoundQnt, setItemNotFoundQnt] = useState(0);
  const [wrongItemFoundQnt, setWrongItemFoundQnt] = useState(0);
  const [damagedItemFoundQnt, setDamagedItemFoundQnt] = useState(0);
  const [partialItemNotFoundQnt, setPartialItemNotFoundQnt] = useState(0);
  const [ofdDbLostDamage, setOfdDbLostDamage] = useState(0);
  const [images, setImages] = useState({
    wrong_item_received: [],
    item_damaged: [],
    partial_not_found: [],
  });

  const onImageChange = (issue, issueImages) => {
    if (images[issue]) {
      setImages((p) => ({
        ...p,
        [issue]: issueImages,
      }));
    }
  };

  const info = route?.params?.info;
  const markingConfig = route?.params?.markingConfig;

  const markingConfigs = transformMarkingArrToObj(markingConfig);

  const imagesMandatoryForSamasya = route?.params?.imagesMandatoryForSamasya;
  const showAddImageWarning = useMemo(() => {
    if (
      (imagesMandatoryForSamasya?.wrong_item_received &&
        wrongItemFoundQnt > 0 &&
        images?.wrong_item_received.length === 0) ||
      (imagesMandatoryForSamasya?.item_damaged &&
        damagedItemFoundQnt > 0 &&
        images?.item_damaged.length === 0) ||
      (imagesMandatoryForSamasya?.partial_not_found &&
        partialItemNotFoundQnt > 0 &&
        images?.partial_not_found.length === 0)
    ) {
      return true;
    }
    return false;
  }, [
    wrongItemFoundQnt,
    damagedItemFoundQnt,
    images,
    imagesMandatoryForSamasya,
    partialItemNotFoundQnt,
  ]);

  const onSubmit = async () => {
    if (showAddImageWarning) {
      toasts.showShortToast("कृप्या फोटो डालें!");
      return;
    }
    if (markingInProcess.current) {
      return;
    }
    markingInProcess.current = true;
    try {
      const new_state = {
        item_damaged: damagedItemFoundQnt,
        wrong_item_received: wrongItemFoundQnt,
        not_found: itemNotFoundQnt,
        partial_not_found: partialItemNotFoundQnt,
        ofd_db_lost_damage: ofdDbLostDamage,
      };

      const payload = {
        order_item_id: info?.order_item_id,
        hash: info?.hash,
        ofd_type: info?.ofd_type,
        type: info?.type,
        diff: getStateDiff(new_state, info?.current_state),
        found_marked: true,
        issue_images: {
          wrong_item_received: images?.wrong_item_received?.map(
            (i) => i?.url && i.url,
          ),
          item_damaged: images?.item_damaged?.map((i) => i?.url && i.url),
          partial_not_found: images?.partial_not_found?.map(
            (i) => i?.url && i.url,
          ),
        },
      };
      console.log(payload);
      await itemsQuantityStatusV3(payload);
      markingInProcess.current = false;
      navigation.goBack();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    setItemNotFoundQnt(info?.current_state?.not_found);
    setWrongItemFoundQnt(info?.current_state?.wrong_item_received);
    setDamagedItemFoundQnt(info?.current_state?.item_damaged);
    setPartialItemNotFoundQnt(info?.current_state?.partial_not_found);
    setOfdDbLostDamage(info?.current_state?.ofd_db_lost_damage);
    setIsLoading(false);
    setImages({
      wrong_item_received:
        info?.wrong_item_received_images?.map((i) => ({ url: i })) || [],
      item_damaged: info?.item_damaged_images?.map((i) => ({ url: i })) || [],
      partial_not_found:
        info?.partial_not_found_images?.map((i) => ({ url: i })) || [],
    });
  }, [info]);

  function ItemNotFound() {
    return (
      <View
        style={{
          paddingLeft: 16,
          paddingRight: 12,
          paddingVertical: 12,
          borderWidth: 1,
          borderColor: "rgba(0, 0, 0, 0.10)",
          borderRadius: 10,
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <CustomText variant="body_text_4" style={{ color: "rgba(0, 0, 0, 1)" }}>
          {"आइटम नहीं मिला "}
        </CustomText>

        {itemNotFoundQnt ? (
          <View
            style={{
              flexDirection: "row",
              backgroundColor: "rgba(255, 46, 0, 1)",
              borderRadius: 8,
              alignItems: "center",
            }}
          >
            <TouchableNativeFeedback
              testID="subtract"
              onPress={() => setItemNotFoundQnt(itemNotFoundQnt - 1)}
            >
              <View style={{ paddingHorizontal: 13, paddingVertical: 9 }}>
                <Minus />
              </View>
            </TouchableNativeFeedback>
            <CustomText
              variant="title_18"
              style={{ color: colors.white, paddingHorizontal: 3 }}
            >
              {itemNotFoundQnt}
            </CustomText>
            <TouchableNativeFeedback
              testID="add"
              onPress={() => {
                if (
                  itemNotFoundQnt +
                    wrongItemFoundQnt +
                    damagedItemFoundQnt +
                    partialItemNotFoundQnt +
                    ofdDbLostDamage <
                  info.total_quantity
                )
                  setItemNotFoundQnt(itemNotFoundQnt + 1);
              }}
            >
              <View style={{ paddingHorizontal: 13, paddingVertical: 9 }}>
                <Plus
                  stroke={
                    itemNotFoundQnt +
                      wrongItemFoundQnt +
                      damagedItemFoundQnt +
                      partialItemNotFoundQnt +
                      ofdDbLostDamage <
                    info.total_quantity
                      ? colors.white
                      : colors.basketBallOrange
                  }
                />
              </View>
            </TouchableNativeFeedback>
          </View>
        ) : (
          <TouchableNativeFeedback
            testID="addQuantity"
            onPress={() => {
              if (
                itemNotFoundQnt +
                  wrongItemFoundQnt +
                  damagedItemFoundQnt +
                  partialItemNotFoundQnt +
                  ofdDbLostDamage <
                info.total_quantity
              )
                setItemNotFoundQnt(itemNotFoundQnt + 1);
            }}
          >
            <View
              style={{
                borderRadius: 8,
                borderWidth: 1,
                borderColor: "rgba(255, 46, 0, 1)",
                paddingHorizontal: 16,
                paddingVertical: 6,
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Plus stroke="rgba(255, 46, 0, 1)" />
              <CustomText
                variant="body_text_4"
                style={{ color: "rgba(255, 46, 0, 1)", marginLeft: 8 }}
              >
                क्वांटिटी
              </CustomText>
            </View>
          </TouchableNativeFeedback>
        )}
      </View>
    );
  }

  function WrongItemFound() {
    return (
      <View
        style={{
          borderWidth: 1,
          borderColor: "rgba(0, 0, 0, 0.10)",
          borderRadius: 10,
          marginTop: 12,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: 16,
            paddingRight: 12,
            paddingVertical: 12,
          }}
        >
          <CustomText
            variant="body_text_4"
            style={{ color: "rgba(0, 0, 0, 1)" }}
          >
            {"गलत आइटम मिला "}
          </CustomText>

          {wrongItemFoundQnt ? (
            <View
              style={{
                flexDirection: "row",
                backgroundColor: "rgba(255, 46, 0, 1)",
                borderRadius: 8,
                alignItems: "center",
              }}
            >
              <TouchableNativeFeedback
                testID="subtract"
                onPress={() => {
                  if (wrongItemFoundQnt === 1)
                    onImageChange("wrong_item_received", []);
                  setWrongItemFoundQnt(wrongItemFoundQnt - 1);
                }}
              >
                <View style={{ paddingHorizontal: 13, paddingVertical: 9 }}>
                  <Minus />
                </View>
              </TouchableNativeFeedback>
              <CustomText
                variant="title_18"
                style={{ color: colors.white, paddingHorizontal: 3 }}
              >
                {wrongItemFoundQnt}
              </CustomText>
              <TouchableNativeFeedback
                testID="add"
                onPress={() => {
                  if (
                    itemNotFoundQnt +
                      wrongItemFoundQnt +
                      damagedItemFoundQnt +
                      partialItemNotFoundQnt +
                      ofdDbLostDamage <
                    info.total_quantity
                  )
                    setWrongItemFoundQnt(wrongItemFoundQnt + 1);
                }}
              >
                <View style={{ paddingHorizontal: 13, paddingVertical: 9 }}>
                  <Plus
                    stroke={
                      itemNotFoundQnt +
                        wrongItemFoundQnt +
                        damagedItemFoundQnt +
                        partialItemNotFoundQnt +
                        ofdDbLostDamage <
                      info.total_quantity
                        ? colors.white
                        : colors.basketBallOrange
                    }
                  />
                </View>
              </TouchableNativeFeedback>
            </View>
          ) : (
            <TouchableNativeFeedback
              testID="addQuantity"
              onPress={() => {
                if (
                  itemNotFoundQnt +
                    wrongItemFoundQnt +
                    damagedItemFoundQnt +
                    partialItemNotFoundQnt +
                    ofdDbLostDamage <
                  info.total_quantity
                )
                  setWrongItemFoundQnt(wrongItemFoundQnt + 1);
              }}
            >
              <View
                style={{
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: "rgba(255, 46, 0, 1)",
                  paddingHorizontal: 16,
                  paddingVertical: 6,
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Plus stroke="rgba(255, 46, 0, 1)" />
                <CustomText
                  variant="body_text_4"
                  style={{ color: "rgba(255, 46, 0, 1)", marginLeft: 8 }}
                >
                  क्वांटिटी
                </CustomText>
              </View>
            </TouchableNativeFeedback>
          )}
        </View>
        <View style={{ paddingLeft: 16, paddingRight: 12 }}>
          {imagesMandatoryForSamasya?.wrong_item_received &&
          wrongItemFoundQnt ? (
            <IssuesImagesUpload
              key="wrongItem_ImagesUpload"
              reason="wrong_item_received"
              imageRequired={true}
              // add from API currentImgs
              currentImgs={images?.wrong_item_received || []}
              setUploadedImage={onImageChange}
              isSingleQty={false}
              info={info}
            />
          ) : null}
        </View>
      </View>
    );
  }

  function DamagedItemFound() {
    return (
      <View
        style={{
          borderWidth: 1,
          borderColor: "rgba(0, 0, 0, 0.10)",
          borderRadius: 10,
          marginTop: 12,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: 16,
            paddingRight: 12,
            paddingVertical: 12,
          }}
        >
          <CustomText
            variant="body_text_4"
            style={{ color: "rgba(0, 0, 0, 1)" }}
          >
            {"डैमेज/ख़राब है "}
          </CustomText>

          {damagedItemFoundQnt ? (
            <View
              style={{
                flexDirection: "row",
                backgroundColor: "rgba(255, 46, 0, 1)",
                borderRadius: 8,
                alignItems: "center",
              }}
            >
              <TouchableNativeFeedback
                testID="subtract"
                onPress={() => {
                  if (damagedItemFoundQnt === 1)
                    onImageChange("item_damaged", []);
                  setDamagedItemFoundQnt(damagedItemFoundQnt - 1);
                }}
              >
                <View style={{ paddingHorizontal: 13, paddingVertical: 9 }}>
                  <Minus />
                </View>
              </TouchableNativeFeedback>
              <CustomText
                variant="title_18"
                style={{ color: colors.white, paddingHorizontal: 3 }}
              >
                {damagedItemFoundQnt}
              </CustomText>
              <TouchableNativeFeedback
                testID="add"
                onPress={() => {
                  if (
                    itemNotFoundQnt +
                      wrongItemFoundQnt +
                      damagedItemFoundQnt +
                      partialItemNotFoundQnt +
                      ofdDbLostDamage <
                    info.total_quantity
                  )
                    setDamagedItemFoundQnt(damagedItemFoundQnt + 1);
                }}
              >
                <View style={{ paddingHorizontal: 13, paddingVertical: 9 }}>
                  <Plus
                    stroke={
                      itemNotFoundQnt +
                        wrongItemFoundQnt +
                        damagedItemFoundQnt +
                        partialItemNotFoundQnt +
                        ofdDbLostDamage <
                      info.total_quantity
                        ? colors.white
                        : colors.basketBallOrange
                    }
                  />
                </View>
              </TouchableNativeFeedback>
            </View>
          ) : (
            <TouchableNativeFeedback
              testID="addQuantity"
              onPress={() => {
                if (
                  itemNotFoundQnt +
                    wrongItemFoundQnt +
                    damagedItemFoundQnt +
                    partialItemNotFoundQnt +
                    ofdDbLostDamage <
                  info.total_quantity
                )
                  setDamagedItemFoundQnt(damagedItemFoundQnt + 1);
              }}
            >
              <View
                style={{
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: "rgba(255, 46, 0, 1)",
                  paddingHorizontal: 16,
                  paddingVertical: 6,
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Plus stroke="rgba(255, 46, 0, 1)" />
                <CustomText
                  variant="body_text_4"
                  style={{ color: "rgba(255, 46, 0, 1)", marginLeft: 8 }}
                >
                  क्वांटिटी
                </CustomText>
              </View>
            </TouchableNativeFeedback>
          )}
        </View>
        <View style={{ paddingLeft: 16, paddingRight: 12 }}>
          {imagesMandatoryForSamasya?.item_damaged && damagedItemFoundQnt ? (
            <IssuesImagesUpload
              key="itemDamaged_ImagesUpload"
              reason="item_damaged"
              imageRequired={true}
              // add from API currentImgs
              currentImgs={images?.item_damaged || []}
              setUploadedImage={onImageChange}
              info={info}
            />
          ) : null}
        </View>
      </View>
    );
  }

  function PartialNotFound() {
    return (
      <View
        style={{
          borderWidth: 1,
          borderColor: "rgba(0, 0, 0, 0.10)",
          borderRadius: 10,
          marginTop: 12,
        }}
      >
        <View
          style={{
            paddingLeft: 16,
            paddingRight: 12,
            paddingVertical: 12,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <CustomText
            variant="body_text_4"
            style={{ color: "rgba(0, 0, 0, 1)" }}
          >
            कुछ आइटम नहीं मिले
          </CustomText>

          {partialItemNotFoundQnt ? (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <CustomText
                variant="title_18"
                style={{ color: colors.white, paddingHorizontal: 3 }}
              >
                {partialItemNotFoundQnt}
              </CustomText>
            </View>
          ) : null}
        </View>
        <View style={{ paddingLeft: 16, paddingRight: 12 }}>
          {imagesMandatoryForSamasya?.partial_not_found &&
          partialItemNotFoundQnt ? (
            <IssuesImagesUpload
              key="partialNotFound_ImagesUpload"
              reason="partial_not_found"
              imageRequired={true}
              // add from API currentImgs
              currentImgs={images?.partial_not_found || []}
              setUploadedImage={onImageChange}
              info={info}
            />
          ) : null}
        </View>
      </View>
    );
  }

  function OfdDbLostDamage() {
    return (
      <View
        style={{
          borderWidth: 1,
          borderColor: "rgba(0, 0, 0, 0.10)",
          borderRadius: 10,
          marginTop: 12,
        }}
      >
        <View
          style={{
            paddingLeft: 16,
            paddingRight: 12,
            paddingVertical: 12,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <CustomText
            variant="body_text_4"
            style={{ color: "rgba(0, 0, 0, 1)" }}
          >
            लॉस्ट
          </CustomText>
          {markingConfigs?.ofd_db_lost_damage_view_only ? (
            <CustomText
              variant="body_text_4"
              style={{ color: "rgba(0, 0, 0, 1)" }}
            >
              {ofdDbLostDamage}
            </CustomText>
          ) : ofdDbLostDamage ? (
            <View
              style={{
                flexDirection: "row",
                backgroundColor: "rgba(255, 46, 0, 1)",
                borderRadius: 8,
                alignItems: "center",
              }}
            >
              <TouchableNativeFeedback
                testID="subtract"
                onPress={() => setOfdDbLostDamage(ofdDbLostDamage - 1)}
              >
                <View style={{ paddingHorizontal: 13, paddingVertical: 9 }}>
                  <Minus />
                </View>
              </TouchableNativeFeedback>
              <CustomText
                variant="title_18"
                style={{ color: colors.white, paddingHorizontal: 3 }}
              >
                {ofdDbLostDamage}
              </CustomText>
              <TouchableNativeFeedback
                testID="add"
                onPress={() => {
                  if (
                    itemNotFoundQnt +
                      wrongItemFoundQnt +
                      damagedItemFoundQnt +
                      partialItemNotFoundQnt +
                      ofdDbLostDamage <
                    info.total_quantity
                  )
                    setOfdDbLostDamage(ofdDbLostDamage + 1);
                }}
              >
                <View style={{ paddingHorizontal: 13, paddingVertical: 9 }}>
                  <Plus
                    stroke={
                      itemNotFoundQnt +
                        wrongItemFoundQnt +
                        damagedItemFoundQnt +
                        partialItemNotFoundQnt +
                        ofdDbLostDamage <
                      info.total_quantity
                        ? colors.white
                        : colors.basketBallOrange
                    }
                  />
                </View>
              </TouchableNativeFeedback>
            </View>
          ) : (
            <TouchableNativeFeedback
              testID="addQuantity"
              onPress={() => {
                if (
                  itemNotFoundQnt +
                    wrongItemFoundQnt +
                    damagedItemFoundQnt +
                    partialItemNotFoundQnt +
                    ofdDbLostDamage <
                  info.total_quantity
                )
                  setOfdDbLostDamage(ofdDbLostDamage + 1);
              }}
            >
              <View
                style={{
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: "rgba(255, 46, 0, 1)",
                  paddingHorizontal: 16,
                  paddingVertical: 6,
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Plus stroke="rgba(255, 46, 0, 1)" />
                <CustomText
                  variant="body_text_4"
                  style={{ color: "rgba(255, 46, 0, 1)", marginLeft: 8 }}
                >
                  क्वांटिटी
                </CustomText>
              </View>
            </TouchableNativeFeedback>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: "white" }}>
      <HeaderWithBackAndTitle
        headertitle="समस्या है ?"
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
      />
      {isLoading ? (
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <>
          <ScrollView
            style={{ flex: 1, paddingHorizontal: 16, marginBottom: 10 }}
          >
            <View
              style={{
                marginBottom: 1,
                borderColor: "rgba(244, 244, 244, 1)",
                borderWidth: 0.5,
                paddingVertical: 20,
                flexDirection: "row",
              }}
            >
              <View>
                <View
                  style={{
                    height: 80,
                    width: 80,
                    borderRadius: 20,
                    borderWidth: 0.5,
                    borderColor: "rgba(0, 0, 0, 0.10)",
                    padding: 8,
                  }}
                >
                  <Image
                    style={{ height: 64, width: 64 }}
                    source={{
                      uri: info?.product_image,
                    }}
                  />
                </View>
                {info?.crate_id ? (
                  <View
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.08)",
                      borderRadius: 10,
                      paddingHorizontal: 8,
                      alignSelf: "center",
                      marginTop: 8,
                    }}
                  >
                    <CustomText
                      variant="body_text_4"
                      style={{ color: "rgba(0, 0, 0, 0.86)", marginTop: 2 }}
                    >{`क्रेट : ${info?.crate_id}`}</CustomText>
                  </View>
                ) : info?.shipment_id ? (
                  <View style={styles.packetInfo}>
                    <CustomText variant="body_text_3" style={styles.packetText}>
                      पैकेट :
                    </CustomText>
                    <CustomText variant="caption_2" style={styles.packetText}>
                      {info?.shipment_id}
                    </CustomText>
                  </View>
                ) : null}
              </View>
              <View style={{ marginTop: 4, marginLeft: 25, flex: 1 }}>
                <CustomText
                  variant="body_text_4"
                  style={{ color: "rgba(0, 0, 0, 0.86)" }}
                >
                  {info?.name}
                </CustomText>
                <View style={{ flexDirection: "row", marginTop: 8 }}>
                  <CustomText
                    variant="body_text_4"
                    style={{ color: "rgba(0, 0, 0, 0.56)" }}
                  >{`क्वांटिटी : `}</CustomText>
                  <CustomText
                    variant="body_text_2"
                    style={{ color: "rgba(0, 0, 0, 0.86)" }}
                  >
                    {info?.total_quantity}
                  </CustomText>
                </View>
              </View>
            </View>
            <View
              style={{ backgroundColor: "rgba(244, 244, 244, 1)", height: 1 }}
            />
            <View style={{ marginTop: 20 }}>
              <CustomText
                variant="title_16"
                style={{ color: "rgba(0, 0, 0, 1)" }}
              >
                {"कृप्या अपनी समस्या बताएं "}
              </CustomText>
              {markingConfigs?.not_found ? <ItemNotFound /> : null}
              <WrongItemFound />
              <DamagedItemFound />
              <PartialNotFound />
              {markingConfigs?.ofd_db_lost_damage ||
              markingConfigs?.ofd_db_lost_damage_view_only ? (
                <OfdDbLostDamage />
              ) : null}
            </View>
          </ScrollView>
          <View
            style={{ paddingHorizontal: 16, width: "100%", display: "flex" }}
          >
            <TouchableNativeFeedback
              testID="aageBadhe"
              onPress={() => onSubmit()}
            >
              <View
                style={{
                  marginBottom: 10,
                  backgroundColor: "rgba(0, 171, 89, 1)",
                  paddingVertical: 10,
                  alignItems: "center",
                  width: "100%",
                  borderRadius: 8,
                }}
              >
                <CustomText variant="title_16" style={{ color: "white" }}>
                  {"आगे बढ़ें "}
                </CustomText>
              </View>
            </TouchableNativeFeedback>
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  packetInfo: {
    backgroundColor: "white",
    borderRadius: 12,
    paddingHorizontal: 8,
    flexDirection: "column",
    alignItems: "center",
    marginTop: 10,
  },
  packetText: {
    color: "black",
    marginTop: 2,
  },
});

export default MultiQuantityIssues;
